import { round } from './round';

// The portion of an invoice that counts against an order.
// By default only positions linked to an order (orderId set) count; positions
// without an orderId are unrelated and must be ignored.
// When the template has useOrderIneffective enabled ("Order Ineffective +
// Order Effective"), the whole invoice counts, so pass includeIneffective.
// Falls back to the backend-provided summary fields when positions are not
// loaded (e.g. invoices coming from the order index endpoint).
// The amount a previous invoice deducts on a final invoice, split into its
// order-effective share and the "not related" remainder.
// Only the order-effective share counts; the remainder is included when the
// template renders order-ineffective amounts (useOrderIneffective). Without
// that flag an invoice with no link to the order deducts 0 by design —
// enable the flag on the template to deduct such invoices.
// Single source of truth for the generated deduction positions, the wizard
// sums and the document preview, so they can never disagree.
export function getInvoiceDeduction(inv, includeIneffective = false) {
  const effective = getOrderEffectiveAmount(inv);
  const full = getOrderEffectiveAmount(inv, true);
  const ineffective = {
    netto: round(full.netto - effective.netto) || 0,
    brutto: round(full.brutto - effective.brutto) || 0,
  };
  const countIneffective = includeIneffective && ineffective.netto > 0;
  return {
    effective,
    ineffective,
    countIneffective,
    netto: effective.netto + (countIneffective ? ineffective.netto : 0),
    brutto: effective.brutto + (countIneffective ? ineffective.brutto : 0),
  };
}

export function getOrderEffectiveAmount(inv, includeIneffective = false) {
  if (Array.isArray(inv?.positions)) {
    return inv.positions.reduce(
      (acc, pos) => {
        if (!includeIneffective && !pos.orderId) return acc;
        const netto = round(pos.price * pos.amount) || 0;
        acc.netto += netto;
        acc.brutto += round(netto * (1 + (pos.taxRate || 0) / 100)) || 0;
        return acc;
      },
      { netto: 0, brutto: 0 },
    );
  }
  if (includeIneffective) {
    return {
      netto: round(inv?.amount || 0) || 0,
      brutto: round(inv?.bruttoAmount || 0) || 0,
    };
  }
  return {
    netto: round(inv?.orderEffectiveAmountNetto || 0) || 0,
    brutto: round(inv?.orderEffectiveAmountBrutto || 0) || 0,
  };
}
